import React from "react";

/**
 * Annotated inline-SVG mockups of Meta's Developer Console screens.
 * Self-hosted so we don't depend on Meta's assets and won't break if their UI shifts.
 * Callout numbers match the numbered instructions in each wizard step.
 */

const C = {
  bg: "#FFFFFF",
  panel: "#FAFAFA",
  border: "#E4E4E7",
  text: "#0A0A0B",
  muted: "#52525B",
  brand: "#4338CA",
  brandLight: "#EEF2FF",
  accent: "#22C55E",
  danger: "#DC2626",
};

function Callout({ x, y, n, color = C.brand }) {
  return (
    <g>
      <circle cx={x} cy={y} r="11" fill={color} />
      <text x={x} y={y + 4} textAnchor="middle" fontSize="12" fontWeight="700" fill="#FFFFFF" fontFamily="system-ui">
        {n}
      </text>
    </g>
  );
}

function HighlightBox({ x, y, w, h, color = C.brand }) {
  return (
    <rect x={x} y={y} width={w} height={h} rx="4" ry="4"
      fill="none" stroke={color} strokeWidth="2" strokeDasharray="4 3" />
  );
}

/** Step 1 — Meta dev dashboard: Create app + WhatsApp product tile + API Setup in sidebar */
export function MetaDashboardVisual() {
  return (
    <div className="border co-hairline rounded-sm bg-white p-2 overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1 px-1">Meta for Developers · Your app dashboard</div>
      <svg viewBox="0 0 480 240" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        {/* Top bar */}
        <rect x="0" y="0" width="480" height="24" fill={C.text} />
        <text x="10" y="16" fontSize="10" fill="#FFFFFF" fontFamily="system-ui" fontWeight="600">Meta for Developers</text>
        <text x="440" y="16" fontSize="9" fill="#A1A1AA" fontFamily="system-ui">My apps ▾</text>

        {/* Left sidebar */}
        <rect x="0" y="24" width="120" height="216" fill={C.panel} />
        <text x="10" y="46" fontSize="9" fill={C.muted} fontFamily="system-ui" fontWeight="700">DASHBOARD</text>
        <text x="10" y="66" fontSize="9" fill={C.muted} fontFamily="system-ui">App settings ▾</text>
        <text x="10" y="86" fontSize="9" fill={C.muted} fontFamily="system-ui">App roles ▾</text>
        <text x="10" y="106" fontSize="9" fill={C.text} fontFamily="system-ui" fontWeight="700">WhatsApp ▾</text>
        <text x="20" y="122" fontSize="9" fill={C.brand} fontFamily="system-ui" fontWeight="700">• API Setup</text>
        <text x="20" y="138" fontSize="9" fill={C.muted} fontFamily="system-ui">Configuration</text>
        <text x="20" y="154" fontSize="9" fill={C.muted} fontFamily="system-ui">Templates</text>
        {/* Highlight API Setup */}
        <HighlightBox x="14" y="112" w="80" h="14" />
        <Callout x={106} y={119} n={5} />

        {/* Main pane */}
        <rect x="120" y="24" width="360" height="216" fill={C.bg} />
        <text x="136" y="48" fontSize="12" fill={C.text} fontFamily="system-ui" fontWeight="700">Add products to your app</text>

        {/* Product tile: WhatsApp */}
        <rect x="136" y="64" width="152" height="80" rx="4" fill={C.bg} stroke={C.border} />
        <circle cx="156" cy="88" r="10" fill={C.accent} />
        <text x="156" y="92" fontSize="10" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">W</text>
        <text x="172" y="86" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">WhatsApp</text>
        <text x="172" y="100" fontSize="8" fill={C.muted} fontFamily="system-ui">Send messages</text>
        <rect x="148" y="114" width="60" height="20" rx="3" fill={C.brand} />
        <text x="178" y="128" fontSize="9" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">Set up</text>
        <Callout x={296} y={124} n={4} />

        {/* Product tile: Others (greyed) */}
        <rect x="300" y="64" width="152" height="80" rx="4" fill={C.panel} stroke={C.border} />
        <circle cx="320" cy="88" r="10" fill={C.border} />
        <text x="336" y="86" fontSize="10" fill={C.muted} fontFamily="system-ui" fontWeight="700">Messenger</text>
        <text x="336" y="100" fontSize="8" fill={C.muted} fontFamily="system-ui">Not needed</text>

        {/* Create app button top-right */}
        <rect x="376" y="36" width="90" height="20" rx="3" fill={C.brand} />
        <text x="421" y="50" fontSize="9" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">+ Create app</text>
        <Callout x={378} y={46} n={2} />

        {/* Footer note */}
        <text x="136" y="180" fontSize="9" fill={C.muted} fontFamily="system-ui">After clicking "Set up", the WhatsApp menu opens in the left sidebar.</text>
      </svg>
      <div className="text-[10px] text-[#71717A] mt-1 px-1 leading-relaxed">
        <b>2</b> Click <b>Create app</b> · <b>4</b> On your app, click <b>Set up</b> on the WhatsApp tile · <b>5</b> Then open <b>WhatsApp → API Setup</b>.
      </div>
    </div>
  );
}

/** Step 2 — API Setup screen with the 3 credential fields circled */
export function ApiSetupVisual() {
  return (
    <div className="border co-hairline rounded-sm bg-white p-2 overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1 px-1">Meta → WhatsApp → API Setup · Send and receive messages</div>
      <svg viewBox="0 0 480 280" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        {/* Card */}
        <rect x="0" y="0" width="480" height="280" fill={C.bg} />
        <rect x="8" y="8" width="464" height="264" rx="6" fill={C.bg} stroke={C.border} />

        {/* Header */}
        <text x="24" y="34" fontSize="12" fill={C.text} fontFamily="system-ui" fontWeight="700">Step 1: Select phone numbers</text>
        <text x="24" y="50" fontSize="9" fill={C.muted} fontFamily="system-ui">To send a WhatsApp message, use the API with your test credentials below.</text>

        {/* Access token row */}
        <text x="24" y="76" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">Temporary access token</text>
        <rect x="24" y="82" width="330" height="26" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="99" fontSize="10" fill={C.muted} fontFamily="monospace">EAAJZC…truncated…kZBwQZDZD</text>
        <rect x="360" y="82" width="96" height="26" rx="3" fill={C.brand} />
        <text x="408" y="99" fontSize="9" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">Generate access token</text>
        <HighlightBox x="358" y="80" w="100" h="30" color={C.danger} />
        <Callout x={470} y={95} n={1} color={C.danger} />

        {/* From dropdown */}
        <text x="24" y="132" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">From</text>
        <rect x="24" y="138" width="220" height="26" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="155" fontSize="10" fill={C.text} fontFamily="system-ui">+91 98765 43210 ▾</text>
        <text x="252" y="155" fontSize="9" fill={C.muted} fontFamily="system-ui">← this is your display number</text>

        {/* Phone number ID row */}
        <text x="24" y="184" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">Phone number ID</text>
        <rect x="24" y="190" width="220" height="26" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="207" fontSize="10" fill={C.muted} fontFamily="monospace">106540359874321</text>
        <rect x="250" y="192" width="22" height="22" rx="3" fill={C.bg} stroke={C.border} />
        <text x="261" y="207" fontSize="10" fill={C.muted} textAnchor="middle" fontFamily="system-ui">⧉</text>
        <HighlightBox x="22" y="188" w="252" h="30" color={C.danger} />
        <Callout x={288} y={203} n={2} color={C.danger} />

        {/* WABA ID row */}
        <text x="24" y="236" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">WhatsApp Business Account ID</text>
        <rect x="24" y="242" width="220" height="26" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="259" fontSize="10" fill={C.muted} fontFamily="monospace">102290128765432</text>
        <rect x="250" y="244" width="22" height="22" rx="3" fill={C.bg} stroke={C.border} />
        <text x="261" y="259" fontSize="10" fill={C.muted} textAnchor="middle" fontFamily="system-ui">⧉</text>
        <HighlightBox x="22" y="240" w="252" h="30" color={C.danger} />
        <Callout x={288} y={255} n={3} color={C.danger} />
      </svg>
      <div className="text-[10px] text-[#71717A] mt-1 px-1 leading-relaxed">
        <b className="text-[#DC2626]">1</b> Click <b>Generate access token</b>, copy the EAA… string ·
        <b className="text-[#DC2626]"> 2</b> Copy <b>Phone number ID</b> ·
        <b className="text-[#DC2626]"> 3</b> Copy <b>WhatsApp Business Account ID</b>.
      </div>
    </div>
  );
}

/** Step 3 — App settings → Basic → App Secret Show button */
export function AppSecretVisual() {
  return (
    <div className="border co-hairline rounded-sm bg-white p-2 overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1 px-1">Meta → App settings → Basic</div>
      <svg viewBox="0 0 480 160" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        <rect x="0" y="0" width="480" height="160" fill={C.bg} />
        <rect x="8" y="8" width="464" height="144" rx="6" fill={C.bg} stroke={C.border} />
        <text x="24" y="32" fontSize="12" fill={C.text} fontFamily="system-ui" fontWeight="700">App settings — Basic</text>

        <text x="24" y="60" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">App ID</text>
        <rect x="24" y="66" width="200" height="24" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="82" fontSize="10" fill={C.muted} fontFamily="monospace">876543210987654</text>

        <text x="24" y="112" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">App secret</text>
        <rect x="24" y="118" width="200" height="24" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="134" fontSize="10" fill={C.muted} fontFamily="monospace">••••••••••••••••••••</text>
        <rect x="230" y="118" width="60" height="24" rx="3" fill={C.brand} />
        <text x="260" y="134" fontSize="10" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">Show</text>
        <HighlightBox x="228" y="116" w="64" h="28" color={C.danger} />
        <Callout x={306} y={130} n={1} color={C.danger} />
      </svg>
      <div className="text-[10px] text-[#71717A] mt-1 px-1 leading-relaxed">
        <b className="text-[#DC2626]">1</b> Click <b>Show</b> next to <b>App secret</b>, confirm your Facebook password, then copy the value.
      </div>
    </div>
  );
}

/** Step 4 — Webhook edit dialog with Callback URL + Verify token */
export function WebhookVisual() {
  return (
    <div className="border co-hairline rounded-sm bg-white p-2 overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1 px-1">Meta → WhatsApp → Configuration → Webhook → Edit</div>
      <svg viewBox="0 0 480 220" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        <rect x="0" y="0" width="480" height="220" fill={C.bg} />
        <rect x="8" y="8" width="464" height="204" rx="6" fill={C.bg} stroke={C.border} />
        <text x="24" y="32" fontSize="12" fill={C.text} fontFamily="system-ui" fontWeight="700">Edit webhook</text>
        <text x="24" y="48" fontSize="9" fill={C.muted} fontFamily="system-ui">Paste the two values from CollectOne below, then subscribe to <tspan fontWeight="700" fill={C.text}>messages</tspan>.</text>

        <text x="24" y="76" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">Callback URL</text>
        <rect x="24" y="82" width="360" height="26" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="99" fontSize="10" fill={C.muted} fontFamily="monospace">https://collectone.in/api/webhooks/whatsapp</text>
        <HighlightBox x="22" y="80" w="364" h="30" color={C.danger} />
        <Callout x={400} y={95} n={1} color={C.danger} />

        <text x="24" y="132" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">Verify token</text>
        <rect x="24" y="138" width="360" height="26" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="155" fontSize="10" fill={C.muted} fontFamily="monospace">co_ws_9f8a2c1e4b7d…</text>
        <HighlightBox x="22" y="136" w="364" h="30" color={C.danger} />
        <Callout x={400} y={151} n={2} color={C.danger} />

        <rect x="24" y="180" width="90" height="22" rx="3" fill={C.brand} />
        <text x="69" y="195" fontSize="10" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">Verify and save</text>
        <text x="124" y="195" fontSize="9" fill={C.muted} fontFamily="system-ui">← then subscribe to "messages"</text>
      </svg>
      <div className="text-[10px] text-[#71717A] mt-1 px-1 leading-relaxed">
        <b className="text-[#DC2626]">1</b> Paste the <b>Callback URL</b> ·
        <b className="text-[#DC2626]"> 2</b> Paste the <b>Verify token</b> · click <b>Verify and save</b>, then tick the <b>messages</b> field.
      </div>
    </div>
  );
}

/** Post-setup task 1 — Subscribe to the "messages" webhook field */
export function SubscribeMessagesVisual() {
  return (
    <div className="border co-hairline rounded-sm bg-white p-2 overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1 px-1">Meta → WhatsApp → Configuration → Webhook fields</div>
      <svg viewBox="0 0 480 200" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        <rect x="0" y="0" width="480" height="200" fill={C.bg} />
        <rect x="8" y="8" width="464" height="184" rx="6" fill={C.bg} stroke={C.border} />
        <text x="24" y="32" fontSize="12" fill={C.text} fontFamily="system-ui" fontWeight="700">Webhook fields</text>
        <text x="24" y="48" fontSize="9" fill={C.muted} fontFamily="system-ui">Choose which events Meta should send to your webhook.</text>

        {/* Row: messages (highlighted) */}
        <rect x="20" y="62" width="440" height="30" rx="3" fill={C.panel} />
        <text x="34" y="80" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">messages</text>
        <text x="34" y="90" fontSize="9" fill={C.muted} fontFamily="system-ui">Incoming client replies + media</text>
        <rect x="360" y="70" width="80" height="18" rx="3" fill={C.brand} />
        <text x="400" y="83" fontSize="9" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">Subscribe ✓</text>
        <HighlightBox x="18" y="60" w="444" h="34" color={C.danger} />
        <Callout x={468} y={77} n={1} color={C.danger} />

        {/* Row: message_status */}
        <rect x="20" y="100" width="440" height="30" rx="3" fill={C.bg} stroke={C.border} />
        <text x="34" y="118" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">message_status</text>
        <text x="34" y="128" fontSize="9" fill={C.muted} fontFamily="system-ui">Delivered / read receipts (recommended)</text>
        <rect x="360" y="108" width="80" height="18" rx="3" fill={C.bg} stroke={C.brand} />
        <text x="400" y="121" fontSize="9" fill={C.brand} textAnchor="middle" fontFamily="system-ui" fontWeight="700">Subscribe</text>
        <Callout x={468} y={117} n={2} />

        {/* Row: account_alerts (skip) */}
        <rect x="20" y="138" width="440" height="30" rx="3" fill={C.bg} stroke={C.border} />
        <text x="34" y="156" fontSize="10" fill={C.muted} fontFamily="system-ui">account_alerts · account_update · phone_number_quality_update</text>
        <text x="440" y="156" fontSize="9" fill={C.muted} textAnchor="end" fontFamily="system-ui">— skip —</text>
      </svg>
      <div className="text-[10px] text-[#71717A] mt-1 px-1 leading-relaxed">
        <b className="text-[#DC2626]">1</b> <b>messages</b> — MUST be on (else no replies arrive) ·
        <b className="text-[#4338CA]"> 2</b> <b>message_status</b> — recommended for delivery ticks.
      </div>
    </div>
  );
}

/** Post-setup task 2 — Register phone number with 6-digit PIN */
export function RegisterPhonePinVisual() {
  return (
    <div className="border co-hairline rounded-sm bg-white p-2 overflow-hidden">
      <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1 px-1">Meta → WhatsApp → API Setup → Manage phone number</div>
      <svg viewBox="0 0 480 180" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
        <rect x="0" y="0" width="480" height="180" fill={C.bg} />
        <rect x="8" y="8" width="464" height="164" rx="6" fill={C.bg} stroke={C.border} />
        <text x="24" y="32" fontSize="12" fill={C.text} fontFamily="system-ui" fontWeight="700">Two-step verification (PIN)</text>
        <text x="24" y="48" fontSize="9" fill={C.muted} fontFamily="system-ui">Set a 6-digit PIN — required before you can send messages via API.</text>

        {/* Phone row */}
        <rect x="24" y="62" width="240" height="26" rx="3" fill={C.panel} stroke={C.border} />
        <text x="34" y="79" fontSize="10" fill={C.text} fontFamily="system-ui">+91 98765 43210</text>
        <text x="272" y="79" fontSize="9" fill={C.muted} fontFamily="system-ui">← your business number</text>

        {/* PIN input */}
        <text x="24" y="112" fontSize="10" fill={C.text} fontFamily="system-ui" fontWeight="700">Set 6-digit PIN</text>
        {[0,1,2,3,4,5].map((i) => (
          <g key={i}>
            <rect x={24 + i*32} y="118" width="26" height="30" rx="3" fill={C.bg} stroke={C.border} />
            <text x={24 + i*32 + 13} y="139" fontSize="14" fill={C.text} textAnchor="middle" fontFamily="monospace" fontWeight="700">•</text>
          </g>
        ))}
        <HighlightBox x="22" y="116" w="196" h="34" color={C.danger} />
        <Callout x={230} y={133} n={1} color={C.danger} />

        <rect x="240" y="120" width="72" height="26" rx="3" fill={C.brand} />
        <text x="276" y="137" fontSize="9" fill="#FFFFFF" textAnchor="middle" fontFamily="system-ui" fontWeight="700">Register</text>
        <Callout x={324} y={133} n={2} color={C.danger} />

        <text x="24" y="168" fontSize="9" fill={C.danger} fontFamily="system-ui">⚠ Save this PIN somewhere safe — Meta will ask for it again.</text>
      </svg>
      <div className="text-[10px] text-[#71717A] mt-1 px-1 leading-relaxed">
        <b className="text-[#DC2626]">1</b> Type any 6-digit PIN · <b className="text-[#DC2626]">2</b> Click <b>Register</b>. Save the PIN — you'll need it if the number is re-registered later.
      </div>
    </div>
  );
}
